import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
    Box, Image, Link as ChakraLink, Text, Heading,
    UnorderedList, OrderedList, ListItem, Divider,
    Modal, ModalOverlay, ModalContent, ModalBody, ModalCloseButton,
} from '@chakra-ui/react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { Capacitor } from '@capacitor/core';
import { AppLauncher } from '@capacitor/app-launcher';
import { resolveImageUrl } from '../services/api';

// 連結一律開「外部瀏覽器」（原生用 AppLauncher，網頁用新分頁）
const openExternal = async (url) => {
    if (!url) return;
    try {
        if (Capacitor.isNativePlatform()) {
            await AppLauncher.openUrl({ url });
        } else {
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    } catch (e) {
        try { window.open(url, '_blank', 'noopener,noreferrer'); } catch (_) { /* ignore */ }
    }
};

// 不需狀態的元件（模組層級，避免每次 render 重建）
const staticComponents = {
    a: ({ href, children }) => (
        <ChakraLink
            color="blue.500"
            textDecoration="underline"
            href={href}
            onClick={(e) => { e.preventDefault(); openExternal(href); }}
        >
            {children}
        </ChakraLink>
    ),
    p: ({ children }) => <Text mb={3} lineHeight="1.8" color="gray.700" whiteSpace="pre-wrap">{children}</Text>,
    h1: ({ children }) => <Heading size="md" my={3} color="gray.800">{children}</Heading>,
    h2: ({ children }) => <Heading size="sm" my={3} color="gray.800">{children}</Heading>,
    h3: ({ children }) => <Heading size="xs" my={2} color="gray.800">{children}</Heading>,
    ul: ({ children }) => <UnorderedList mb={3} pl={5}>{children}</UnorderedList>,
    ol: ({ children }) => <OrderedList mb={3} pl={5}>{children}</OrderedList>,
    li: ({ children }) => <ListItem lineHeight="1.8" color="gray.700">{children}</ListItem>,
    hr: () => <Divider my={4} />,
    strong: ({ children }) => <Text as="strong" fontWeight="bold">{children}</Text>,
};

/**
 * 公告內文 Markdown 渲染：圖片（![](url)，可點放大）+ 可點連結（[文字](url)→外部瀏覽器）。
 */
export default function MarkdownContent({ content }) {
    const [selectedImage, setSelectedImage] = useState(null);
    if (!content) return null;

    const components = {
        ...staticComponents,
        img: ({ src, alt }) => {
            const url = resolveImageUrl(src);
            return (
                <Image
                    src={url}
                    alt={alt || ''}
                    borderRadius="lg"
                    my={3}
                    w="100%"
                    objectFit="contain"
                    cursor="zoom-in"
                    onClick={() => setSelectedImage(url)}
                />
            );
        },
    };

    return (
        <Box sx={{ wordBreak: 'break-word' }}>
            <ReactMarkdown components={components}>{content}</ReactMarkdown>

            {/* 圖片放大 Lightbox */}
            <Modal isOpen={!!selectedImage} onClose={() => setSelectedImage(null)} size="full" isCentered>
                <ModalOverlay bg="blackAlpha.900" />
                <ModalContent bg="transparent" boxShadow="none" m={0}>
                    <ModalCloseButton
                        color="white"
                        bg="blackAlpha.600"
                        _hover={{ bg: 'blackAlpha.800' }}
                        borderRadius="full"
                        zIndex={200}
                        size="lg"
                        position="fixed"
                        top="calc(env(safe-area-inset-top) + 12px)"
                        right="16px"
                    />
                    <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
                        {selectedImage && (
                            <TransformWrapper doubleClick={{ mode: 'reset' }}>
                                <TransformComponent
                                    wrapperStyle={{ width: '100vw', height: '100vh' }}
                                    contentStyle={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <Image src={selectedImage} w="100vw" maxH="100vh" objectFit="contain" />
                                </TransformComponent>
                            </TransformWrapper>
                        )}
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
}
